import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import { getProductsInShoppingCart, getTotal } from 'reducers/ShoppingCart';
import { isEmpty } from 'utils';
import * as c from 'constants/sales';
import * as c2 from 'constants/customerSearch';
import { lastReceiptData } from 'services/localDB';
// eslint-disable-next-line import/no-cycle
import { TYPE_LOGOUT, TYPE_LOGOUT_SOFT } from 'constants/Login';

import { getIsModalOpened, getCurrentComponent } from './modalPage';
import { getErrorsToShow } from './Error';

const initState = {};

initState.all = [];

const all = (state = initState.all, { type, payload }) => {
  switch (type) {
    case c.GET_SALES_DOCUMENTS_SUCCESS:
      return payload;
    default:
      return state;
  }
};

/**
 * State only used for recordsTotal - todo: simplify or merge into something - made selectors
 */
initState.fullResponse = { status: { recordsTotal: 0 } };

const fullResponse = (state = initState.fullResponse, { type, payload }) => {
  switch (type) {
    case c.GET_SALES_DOCUMENTS_FULL_RESPONSE_SUCCESS:
      return payload;
    case c2.RESET_CUSTOMER:
    case TYPE_LOGOUT:
    case TYPE_LOGOUT_SOFT:
      return initState.fullResponse;
    default:
      return state;
  }
};

initState.currentSale = {};

const currentSale = (state = {}, { type, payload }) => {
  switch (type) {
    case c.SET_CURRENT_SALE_DOC:
      return payload;
    case c.UPDATE_CURRENT_SALE_DOC:
      return { ...state, ...payload };
    case c.SET_SALE_EMPLOYEE:
      return { ...state, employee: payload };
    case c.SAVE_SALES_DOCUMENT.SUCCESS:
    case c.RESET_CURRENT_SALES_DOC:
    case TYPE_LOGOUT:
    case TYPE_LOGOUT_SOFT:
      return initState.currentSale;
    case c.SET_USED_REWARD_POINTS:
      return {
        ...state,
        usedRewardPoints: payload,
      };
    default:
      return state;
  }
};

initState.paymentSale = {};

const paymentSale = (state = {}, { type, payload }) => {
  switch (type) {
    case c.SET_PAYMENT_SALE_DOC:
      return payload;
    case c.UPDATE_PAYMENT_SALE_DOC:
      return { ...state, ...payload };
    case c.RESET_PAYMENT_SALES_DOC:
    case TYPE_LOGOUT:
    case TYPE_LOGOUT_SOFT:
      return initState.paymentSale;
    default:
      return state;
  }
};

initState.lastSale = {};
const lastSale = (state = {}, { type, payload }) => {
  switch (type) {
    case c.SET_LAST_SALES_DOC:
      return payload;
    case c.RESET_LAST_SALES_DOC:
    case TYPE_LOGOUT:
    case TYPE_LOGOUT_SOFT:
      return initState.lastSale;
    default:
      return state;
  }
};

const lastPayments = (state = {}, { type, payload }) => {
  switch (type) {
    case c.SET_LAST_PAYMENTS:
      return payload;
    case c.DELETE_LAST_PAYMENT_BY_KEY:
      return state.filter(payment => payment.key !== payload);
    default:
      return state;
  }
};

initState.fetching = false;

const fetching = (state = initState.fetching, { type }) => {
  switch (type) {
    case c.GET_SALES_DOCUMENTS:
      return true;
    case c.GET_SALES_DOCUMENTS_SUCCESS:
    case c.GET_SALES_DOCUMENTS_FULL_RESPONSE_SUCCESS:
    case c.GET_SALES_DOCUMENTS_ERROR:
      return initState.fetching;
    default:
      return state;
  }
};

initState.isCurrentSaleAReturn = false;
const isCurrentSaleAReturn = (
  state = initState.isCurrentSaleAReturn,
  { type, payload },
) => {
  switch (type) {
    case c.SET_CURRENT_SALE_AS_RETURN:
      return payload;
    case c.SET_CURRENT_SALE_DOC:
    case c.RESET_CURRENT_SALE_AS_RETURN:
    case TYPE_LOGOUT:
    case TYPE_LOGOUT_SOFT:
      return initState.isCurrentSaleAReturn;
    default:
      return state;
  }
};

initState.currentSalesDocPayments = { original: [], returned: [] };
const currentSalesDocPayments = (
  state = initState.currentSalesDocPayments,
  { type, payload },
) => {
  switch (type) {
    case c.SET_CURRENT_SALES_DOC_PAYMENTS.SUCCESS:
      return payload;
    case c.RESET_CURRENT_SALES_DOC_PAYMENTS:
    case TYPE_LOGOUT:
    case TYPE_LOGOUT_SOFT:
      return initState.currentSalesDocPayments;
    default:
      return state;
  }
};

const prepaymentPercentages = (state = [], { type, payload }) => {
  switch (type) {
    case c.GET_PREPAYMENT_PERCENTAGES.SUCCESS:
      return payload;
    default:
      return state;
  }
};

initState.lastSaleActualReportsDataset = null;
const lastSaleActualReportsDataset = (state = {}, { type, payload }) => {
  switch (type) {
    case c.SET_LAST_SALE_ACTUAL_REPORTS_DATASET:
      return payload;
    case c.RESET_LAST_SALES_DOC:
    case TYPE_LOGOUT:
    case TYPE_LOGOUT_SOFT:
      return initState.lastSale;
    default:
      return state;
  }
};

export default combineReducers({
  all,
  currentSale,
  fullResponse,
  fetching,
  lastSale,
  isCurrentSaleAReturn,
  prepaymentPercentages,
  currentSalesDocPayments,
  paymentSale,
  lastPayments,
  lastSaleActualReportsDataset,
});

const getSalesSlice = state => state.sales;

export const getTotalVisitsToStore = createSelector(
  getSalesSlice,
  sales => sales.fullResponse.status?.recordsInResponse ?? 0,
);

export const getPreviousPurchases = createSelector(
  getSalesSlice,
  sales => sales.fullResponse.records,
);

export const getCurrentSalesDocument = state => state.sales.currentSale || {};

export const getLastPayments = state => state.sales.lastPayments || [];

export const getHasCurrentSalesDocument = state =>
  !isEmpty(state.sales.currentSale);

export const getIsCurrentSaleAReturn = state =>
  state.sales.isCurrentSaleAReturn;

export const getIsSavedSale = createSelector(
  state => getCurrentSalesDocument(state),
  currentDoc =>
    currentDoc?.type === 'CASHINVOICE' &&
    Number(currentDoc.confirmed) === 0 &&
    currentDoc.paymentStatus === 'UNPAID',
);

export const getCurrentSalesDocPayments = state =>
  state.sales.currentSalesDocPayments.original;

export const getCurrentSalesDocReturnPayments = state =>
  state.sales.currentSalesDocPayments.returned;

export function getIsAReturn(state) {
  return getIsCurrentSaleAReturn(state) || getTotal(state) < 0;
}

export const getIsUnreferencedReturn = state =>
  !getIsCurrentSaleAReturn(state) && getTotal(state) < 0;

export const getReturnTotal = state => state.shoppingCart.computed.total ?? 0;

export const getReturnVATTotal = state =>
  state.shoppingCart.computed.vatTotal ?? 0;

export const getReturnNetTotal = state =>
  state.shoppingCart.computed.netTotal ?? 0;

export const getLastSalesDocument = createSelector(
  () => lastReceiptData.get(),
  lastReceiptData => {
    if (!lastReceiptData) {
      return { salesDoc: {}, payments: {} };
    }
    if (isEmpty(lastReceiptData)) {
      return { salesDoc: {}, payments: {} };
    }

    return lastReceiptData;
  },
);

export const getLastSale = state => {
  return state.sales.lastSale ?? {};
};

export const getReturnIsPartial = state => {
  const productsInCart = getProductsInShoppingCart(state);
  const currentSalesDocument = getCurrentSalesDocument(state);
  const isReturn = getIsAReturn(state);

  const isFullReturn =
    !currentSalesDocument?.previousReturnsExist &&
    currentSalesDocument?.rows?.length === productsInCart.length &&
    currentSalesDocument?.rows?.reduce((result, currentProduct, index) => {
      const isProductInCart = (() => {
        if (productsInCart[index]) {
          const cartAmount = Number(productsInCart[index].amount);
          const invoiceAmount = -Number(currentProduct.amount);
          return cartAmount === invoiceAmount;
        }
        return false;
      })();
      return result && isProductInCart;
    }, true);

  return isReturn && !isFullReturn;
};

export const getShouldBlockNewSale = state =>
  (getIsModalOpened(state) &&
    getCurrentComponent(state)?.groupID !== 'GridModal') ||
  getErrorsToShow(state).some(err => err.name === 'savingDocument');

export function getLastSaleActualReportsDataset(state) {
  return state.sales.lastSaleActualReportsDataset;
}
